import { useMutation, useQuery, useQueryClient, type DefaultError, type UseMutationOptions, type UseMutationResult } from '@tanstack/react-query';
import { useRouter, type RegisteredRouter } from '@tanstack/react-router';
import { createContext, useContext, useEffect, type ReactNode } from 'react';

import type { Messages } from '../i18n';
import { ApiError, endpoints, type Me } from './api';

export const SESSION_KEY = ['session'] as const;

/**
 * What a session meets on a page that needs a permission (ADR 0015 §4): the
 * page opens; the company's package leaves it out, which a change of package
 * mends rather than an owner; or the role refuses it.
 */
export type PageAccess = 'open' | 'not_in_plan' | 'forbidden';

export interface Session {
    me: Me | null;
    isLoading: boolean;
    /** True once a company is selected and the panel can show tenant data. */
    isReady: boolean;
    can: (permission: string) => boolean;
    /**
     * What a page needing the permission meets, a page that needs none being
     * open: one answer for the shell's refusal and for a page that sends the
     * seller to another (docs/11 §8).
     */
    access: (permission: string | undefined) => PageAccess;
}

const SessionContext = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
    const queryClient = useQueryClient();

    const { data, isLoading } = useQuery({
        queryKey: SESSION_KEY,
        queryFn: endpoints.me,
        // A 401 is a definitive answer, not a transient failure; retrying it would
        // only delay the redirect to the sign-in screen.
        retry: (failureCount, error) => !(error instanceof ApiError && error.isUnauthenticated) && failureCount < 2,
        staleTime: 30_000,
        // Staff and the hourly billing job move a subscription to Süresi Doldu
        // while a tab is open, and the permissions that follow it ride on this
        // same answer. The staleTime caps the focus refetch at one /v1/me per 30
        // seconds. A signed-out screen is not polled: it has no session to keep
        // current, and each poll would only be another 401.
        refetchOnWindowFocus: true,
        refetchInterval: (query) => (query.state.data ? 60_000 : false),
    });

    // The client emits this when any request comes back unauthenticated, so a
    // session revoked in another tab clears here too.
    useEffect(() => {
        const onExpired = () => queryClient.setQueryData(SESSION_KEY, null);
        window.addEventListener('session:expired', onExpired);
        return () => window.removeEventListener('session:expired', onExpired);
    }, [queryClient]);

    const me = data ?? null;
    const granted = new Set(me?.permissions ?? []);
    const withheld = new Set(me?.withheldPermissions ?? []);

    const value: Session = {
        me,
        isLoading,
        isReady: Boolean(me?.company),
        // Convenience only. The panel hides what a user cannot do; the server
        // still refuses it. A client-side check is never the enforcement point.
        can: (permission: string) => granted.has(permission),
        // A permission the role grants and the plan leaves out is withheld.
        access: (permission: string | undefined) =>
            permission === undefined || granted.has(permission) ? 'open' : withheld.has(permission) ? 'not_in_plan' : 'forbidden',
    };

    return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
    const session = useContext(SessionContext);
    if (!session) {
        throw new Error('useSession must be used inside SessionProvider');
    }
    return session;
}

/** A page's address, as its route declares it. */
export type PagePath = keyof RegisteredRouter['routesByPath'];

/** A permission the API grants to a role, as the dictionary words it. */
export type Permission = keyof Messages['permissions'];

/**
 * What the session meets on a page, from the permission its route names
 * (`staticData.permission` in main.tsx), the only place that says so. A page
 * that sends the seller to another asks it, so a way the package leaves out is
 * said rather than hidden (docs/11 §8).
 */
export function usePageAccess(): (to: PagePath) => PageAccess {
    const router = useRouter();
    const { access } = useSession();
    return (to) => access(router.routesByPath[to].options.staticData?.permission);
}

/**
 * Whether the session may open a page: the menu and the section tabs ask here
 * before offering a route, and the shell refuses a route the answer is no for.
 */
export function useCanOpen(): (to: PagePath) => boolean {
    const pageAccess = usePageAccess();
    return (to) => pageAccess(to) === 'open';
}

/**
 * A write that names the permission its endpoint requires, in the data hook
 * that performs it and nowhere else. A screen draws the action only when
 * `allowed` is true (docs/11 §2); the server still refuses everyone else.
 * `npm run check` holds the name to the endpoint handler's own check.
 */
export function usePermittedMutation<TData = unknown, TError = DefaultError, TVariables = void, TContext = unknown>(
    permission: Permission,
    options: UseMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> & { allowed: boolean } {
    const { can } = useSession();
    const mutation = useMutation(options);
    return { ...mutation, allowed: can(permission) };
}

export function useLogin() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: endpoints.login,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSION_KEY }),
    });
}

/** The code that finishes a sign-in waiting for its second step. */
export function useCompleteSecondFactor() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: endpoints.completeSecondFactor,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSION_KEY }),
    });
}

export function useRegister() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: endpoints.register,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSION_KEY }),
    });
}

export function useLogout() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: endpoints.logout,
        // Everything cached belongs to the session that just ended. Clearing it
        // avoids showing one company's data to whoever signs in next on a shared
        // machine.
        onSettled: () => queryClient.clear(),
    });
}

/**
 * Accepting the current terms for the selected company (ADR 0018 §1). Every
 * company request is refused until it is done, so the whole session is read
 * again once it is.
 */
export function useAcceptTerms() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: endpoints.acceptTerms,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSION_KEY }),
    });
}

export function useSelectCompany() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: endpoints.selectCompany,
        onSuccess: () => queryClient.invalidateQueries(),
    });
}
